import { useId, useRef, useState } from 'react';
import { ApiError } from '../../identity/types';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { peopleGateway } from '../index';
import type { DepartmentOption } from '../types';

export interface AddDepartmentDialogProps {
  /** The departments already listed, used only to tell "added" from "already existed". */
  existing: DepartmentOption[];
  onClose: () => void;
  /** Called with the saved department; `existed` is true when the company already had one with that name (nothing is duplicated). */
  onAdded: (department: DepartmentOption, existed: boolean) => void;
}

/**
 * The small "Add New Department" dialog opened from Add Employee and from Create new role. The department is SAVED for the caller's own
 * company by the server, so it is available for every later employee and role. It sits on top of the dialog that opened it, which stays
 * mounted underneath, so nothing already typed is lost. The server decides permission, uniqueness and audit; a refusal is shown here.
 */
export function AddDepartmentDialog({ existing, onClose, onAdded }: AddDepartmentDialogProps) {
  const uid = useId();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  async function submit() {
    const clean = name.replace(/\s+/g, ' ').trim();
    if (!clean) {
      setNameError('Enter a department name.');
      nameRef.current?.focus();
      return;
    }
    if (clean.length > 100) {
      setNameError('The department name must be 100 characters or fewer.');
      nameRef.current?.focus();
      return;
    }
    setNameError(null);
    setError(null);
    setBusy(true);
    try {
      const created = await peopleGateway.createDepartment(clean);
      onAdded(created, existing.some((d) => d.id === created.id));
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'You do not have permission to add departments. Ask a company administrator to add it, or choose an existing department.'
          : friendlyErrorMessage(err, err instanceof Error && !(err instanceof ApiError) ? err.message : 'Could not add the department.'),
      );
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="Add New Department"
      size="sm"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy}>{busy ? 'Adding…' : 'Add Department'}</button>
        </>
      )}
    >
      <p className="muted">It is saved for your company and will be available when you add other employees. Everything you have entered so far is kept.</p>
      <div className="jr-field">
        <label htmlFor={`${uid}-name`}>Department name <span aria-hidden="true" className="jr-req">*</span></label>
        <input
          ref={nameRef} id={`${uid}-name`} data-autofocus type="text" value={name} maxLength={100} autoComplete="off" aria-required="true"
          aria-invalid={nameError ? true : undefined} aria-describedby={nameError ? `${uid}-name-error` : undefined}
          onChange={(e) => { setName(e.target.value); setNameError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
        />
        {nameError && <p id={`${uid}-name-error`} className="jr-error" role="alert">{nameError}</p>}
      </div>
      {error && <p className="jr-error" role="alert">{error}</p>}
    </ModalDialog>
  );
}
